import React from "react";

export interface IStatusPaginationComponentProps {
  onPageChange: (pageNumber: number, pageSize: number) => void;
  totalCount: number;
  currentPage: number;
  recordsPerPage: number;
}

type PageToken = number | "ellipsis";

function buildPageTokens(
  currentPage: number,
  totalPageCount: number
): PageToken[] {
  if (totalPageCount <= 7) {
    return Array.from({ length: totalPageCount }, (_, index) => index + 1);
  }

  if (currentPage <= 4) {
    const end = Math.min(Math.max(4, currentPage + 1), totalPageCount - 1);
    const tokens: PageToken[] = [];
    for (let page = 1; page <= end; page += 1) {
      tokens.push(page);
    }
    if (end < totalPageCount - 1) {
      tokens.push("ellipsis");
    }
    if (end < totalPageCount) {
      tokens.push(totalPageCount);
    }
    return tokens;
  }

  if (currentPage >= totalPageCount - 3) {
    const start = totalPageCount - 4;
    const tokens: PageToken[] = [1, "ellipsis"];
    for (let page = start; page <= totalPageCount; page += 1) {
      tokens.push(page);
    }
    return tokens;
  }

  return [
    1,
    "ellipsis",
    currentPage - 1,
    currentPage,
    currentPage + 1,
    "ellipsis",
    totalPageCount,
  ];
}

const StatusPaginationComponent: React.FC<IStatusPaginationComponentProps> = ({
  totalCount,
  onPageChange,
  currentPage,
  recordsPerPage,
}) => {
  const totalPageCount = Math.max(1, Math.ceil(totalCount / recordsPerPage));
  const pageTokens = buildPageTokens(currentPage, totalPageCount);

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1, recordsPerPage);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPageCount) {
      onPageChange(currentPage + 1, recordsPerPage);
    }
  };

  const inactivePageClassName =
    "text-base font-bold leading-none text-profile-dark-blue transition-colors hover:text-Default";
  const arrowClassName =
    "text-lg font-bold leading-none text-profile-dark-blue transition-colors hover:text-Default disabled:cursor-default disabled:opacity-40";

  return (
    <div className="flex w-full justify-end py-6 font-sans">
      <div className="flex flex-col items-end gap-4 sm:flex-row sm:items-center sm:gap-8">
        <p className="shrink-0 whitespace-nowrap text-lg font-bold leading-none text-Default">
          Page {currentPage} of {totalPageCount}
        </p>

        <div className="flex shrink-0 flex-nowrap items-center gap-3">
        {currentPage > 1 ? (
          <button
            type="button"
            className={arrowClassName}
            onClick={handlePreviousPage}
            aria-label="Previous page"
          >
            &lt;
          </button>
        ) : null}

        {pageTokens.map((token, index) => {
          if (token === "ellipsis") {
            return (
              <span
                key={`ellipsis-${index}`}
                className={inactivePageClassName}
                aria-hidden="true"
              >
                ...
              </span>
            );
          }

          if (token === currentPage) {
            return (
              <span
                key={token}
                className="flex h-8 w-8 items-center justify-center rounded-full bg-Default text-base font-bold leading-none text-white"
                aria-current="page"
              >
                {token}
              </span>
            );
          }

          return (
            <button
              key={token}
              type="button"
              className={inactivePageClassName}
              onClick={() => onPageChange(token, recordsPerPage)}
            >
              {token}
            </button>
          );
        })}

        <button
          type="button"
          className={arrowClassName}
          onClick={handleNextPage}
          disabled={currentPage >= totalPageCount}
          aria-label="Next page"
        >
          &gt;
        </button>
        </div>
      </div>
    </div>
  );
};

export default StatusPaginationComponent;
